import type React from "react"
import { Link } from "react-router-dom"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"
import { ArrowRight, Leaf, RouteIcon as Road, Snowflake, Building, Plane, Users } from "lucide-react"

const ApplicationsPage: React.FC = () => {
  const applications = [
    {
      title: "Ground Care Applications",
      description: "Professional landscaping, sports field maintenance, and vegetation management solutions",
      icon: Leaf,
      href: "/applications/ground-care",
      image: "https://placehold.co/400x300?text=Ground+Care",
      features: ["Sports Field Maintenance", "Landscaping", "Vegetation Management", "Park Maintenance"],
      color: "green",
    },
    {
      title: "Road Maintenance",
      description: "Comprehensive road and infrastructure maintenance for highways, streets, and pathways",
      icon: Road,
      href: "/applications/road-maintenance",
      image: "https://placehold.co/400x300?text=Road+Maintenance",
      features: ["Street Sweeping", "Surface Cleaning", "Patch Planning", "Line Removal"],
      color: "blue",
    },
    {
      title: "Winter Operations",
      description: "Complete winter maintenance solutions for snow removal, de-icing, and cold weather operations",
      icon: Snowflake,
      href: "/applications/winter",
      image: "https://placehold.co/400x300?text=Winter+Operations",
      features: ["Snow Plowing", "De-icing", "Salt Spreading", "Ice Breaking"],
      color: "cyan",
    },
    {
      title: "Municipal Services",
      description: "Versatile solutions for city and town maintenance, sanitation, and public works",
      icon: Building,
      href: "/applications/municipal",
      image: "https://placehold.co/400x300?text=Municipal+Services",
      features: ["City Sanitation", "Public Works", "Facility Maintenance", "Urban Cleaning"],
      color: "purple",
    },
    {
      title: "Airport Operations",
      description: "Specialized equipment for runway maintenance, ground support, and airport facility management",
      icon: Plane,
      href: "/applications/airport",
      image: "https://placehold.co/400x300?text=Airport+Operations",
      features: ["Runway Cleaning", "Glycol Recovery", "Ground Support", "Light Maintenance"],
      color: "orange",
    },
    {
      title: "Facility Management",
      description: "Commercial and industrial facility maintenance for universities, schools, and large complexes",
      icon: Users,
      href: "/applications/facility-management",
      image: "https://placehold.co/400x300?text=Facility+Management",
      features: ["Campus Maintenance", "Industrial Cleaning", "Grounds Keeping", "Seasonal Care"],
      color: "red",
    },
  ]

  const getColorClasses = (color: string) => {
    const colorMap = {
      green: "bg-green-100 text-green-800 border-green-200",
      blue: "bg-blue-100 text-blue-800 border-blue-200",
      cyan: "bg-cyan-100 text-cyan-800 border-cyan-200",
      purple: "bg-purple-100 text-purple-800 border-purple-200",
      orange: "bg-orange-100 text-orange-800 border-orange-200",
      red: "bg-red-100 text-red-800 border-red-200",
    }
    return colorMap[color as keyof typeof colorMap] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="mb-6 inline-block bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-full text-sm font-medium">
              Versatile Applications
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              <span className="text-red-500">One Machine,</span>
              <br />
              Endless Applications
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
              From municipal services to specialized industrial applications, Multihog tractors deliver unmatched
              versatility across every season and environment.
            </p>
          </div>
        </div>
      </section>

      {/* Applications Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Explore Our Applications</h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Discover how Multihog tractors and specialized attachments deliver exceptional performance across
                diverse industries and applications.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {applications.map((app, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="relative">
                    <img src={app.image || "/placeholder.svg"} alt={app.title} className="w-full h-48 object-cover" />
                    <div className="absolute top-4 left-4">
                      <div className={`p-2 rounded-lg ${getColorClasses(app.color)}`}>
                        <app.icon className="h-6 w-6" />
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{app.title}</h3>
                    <p className="text-gray-600 mb-4 leading-relaxed">{app.description}</p>

                    <div className="mb-6">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Applications:</h4>
                      <div className="flex flex-wrap gap-2">
                        {app.features.map((feature, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs"
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>

                    <Link
                      to={app.href}
                      className="inline-flex items-center text-red-600 hover:text-red-700 font-medium transition-colors"
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Proven Performance Across Industries</h2>
              <p className="text-lg text-gray-600">
                Trusted by professionals worldwide for reliable, efficient operations
              </p>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-red-600 mb-2">40+</div>
                <div className="text-gray-600">Specialized Attachments</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-red-600 mb-2">6</div>
                <div className="text-gray-600">Core Applications</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-red-600 mb-2">365</div>
                <div className="text-gray-600">Days Per Year Operation</div>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-sm">
                <div className="text-3xl font-bold text-red-600 mb-2">20+</div>
                <div className="text-gray-600">Years of Innovation</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Operations?</h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto">
              Discover how Multihog tractors can revolutionize your specific application. Our experts are ready to help
              you find the perfect solution.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-red-600 hover:bg-gray-100 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                Get Application Consultation
              </Link>
              <Link
                to="/models"
                className="border border-white text-white hover:bg-white hover:text-red-600 px-8 py-4 text-lg font-medium rounded-lg transition-colors"
              >
                View Our Models
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ApplicationsPage
